/* AppraisalComponents/EngineStatusBadge.jsx
 * Phase C · C4h (2026-02c) — Rules engine health chip.
 *
 * Small header adornment next to the Components tab title that tells
 * the operator whether the rules engine is driving the form or the
 * legacy imperative fallbacks are in play. Three states:
 *
 *   🟢 Engine active   → engine loaded · N rules fired for this appraisal
 *   🟠 Loading…        → engine responding · debounce in flight
 *   🔴 Engine offline  → hook returned no result (backend down / cache miss)
 *
 * Purely presentational · props-in, JSX-out · reads state from the
 * parent's `useCascadeEngine` hook.
 */
import React from 'react';

export function EngineStatusBadge({
  loading,
  rulesFired,
  rulesEvaluated,
  engineVersionHash,
  hasBlockingErrors,
}) {
  // Priority order: blocking-error > loading > offline > active.
  let dotColour, label, ring;
  if (hasBlockingErrors) {
    dotColour = 'bg-rose-500';
    ring = 'ring-rose-200 bg-rose-50 text-rose-800';
    // C4-audit UX nuance (2026-02c) — retain the fired count in the
    // label when the engine is blocking, so operators can still see
    // "engine did fire · but it's flagged errors" instead of assuming
    // the engine went dead.
    label = rulesFired > 0
      ? `Engine · ${rulesFired} fired · blocking`
      : 'Engine · blocking';
  } else if (loading) {
    dotColour = 'bg-amber-500 animate-pulse';
    ring = 'ring-amber-200 bg-amber-50 text-amber-800';
    label = 'Engine · loading';
  } else if (rulesEvaluated === 0) {
    dotColour = 'bg-slate-400';
    ring = 'ring-slate-200 bg-slate-50 text-slate-700';
    label = 'Engine · idle';
  } else {
    dotColour = 'bg-emerald-500';
    ring = 'ring-emerald-200 bg-emerald-50 text-emerald-800';
    label = 'Engine active';
  }

  const title = [
    `Rules fired: ${rulesFired} / ${rulesEvaluated}`,
    engineVersionHash ? `Version: ${engineVersionHash.slice(0, 8)}` : null,
    hasBlockingErrors ? 'Blocking validation errors present · save disabled.' : null,
  ].filter(Boolean).join(' · ');

  return (
    <span
      data-testid="engine-status-badge"
      className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 font-mono-num ${ring}`}
      title={title}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotColour}`} />
      {label}
      {rulesFired > 0 && !loading && !hasBlockingErrors && (
        <span className="opacity-70"> · {rulesFired} fired</span>
      )}
    </span>
  );
}
